import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnInit,
  Output,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { EventApi, EventClickArg } from '@fullcalendar/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import moment from 'moment-timezone';

import { ProgramsState } from '../../../../../programs/src/lib/states/programs.state';
import { SchedulerCoreState } from '../../../../../scheduler-core/src/lib/states/scheduler-core.state';
import {
  SlotKind,
  SlotPlaylist,
  SlotPlayType,
  SlotProgram,
  SlotRepeat,
} from '../../models/planning.model';

@Component({
  selector: 'rk-planning-slot-popover',
  templateUrl: './planning-slot-popover.component.html',
  styleUrls: ['./planning-slot-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningSlotPopoverComponent implements OnInit, AfterViewInit {
  event: EventApi;

  program?: SlotProgram;

  playlist?: SlotPlaylist;

  formattedDateRange = '-';

  playTypeIcon = '';

  playTypeText = '';

  repeatText = '';

  repeatWeekday = '';

  @Output()
  readonly slotDeletion = new EventEmitter<EventApi>();

  @Output()
  readonly slotEdition = new EventEmitter<EventApi>();

  get slotKind(): SlotKind {
    return this.event.extendedProps.kind;
  }

  get slotProgram(): SlotProgram['id'] {
    return this.event.extendedProps.program;
  }

  get slotPlaylist(): SlotPlaylist['id'] {
    return this.event.extendedProps.playlist;
  }

  get slotPlayType(): SlotPlayType {
    return this.event.extendedProps.playType;
  }

  get slotRepeat(): SlotRepeat {
    return this.event.extendedProps.repeat;
  }

  get dialogDimensions(): { width: number; height: number; margin: number } {
    const { width, height } =
      this.elementRef.nativeElement.firstElementChild.getBoundingClientRect();
    const margin = 10; // See `.planning-slot-popover` styles

    return {
      width,
      height: height + margin * 2,
      margin,
    };
  }

  get clickInfo(): EventClickArg {
    return this.data.clickInfo;
  }

  constructor(
    private readonly store: Store,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly translate: TranslateService,
    private readonly dialogRef: MatDialogRef<PlanningSlotPopoverComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { clickInfo: EventClickArg; mobile?: boolean },
  ) {
    this.event = data.clickInfo.event;
  }

  ngOnInit(): void {
    this.extractProgram();
    this.extractPlaylist();
    this.extractPlayTypeIcon();
    this.extractPlayTypeText();
    this.formatDateRange();
    this.extractRepeatText();
  }

  ngAfterViewInit(): void {
    if (!this.data.mobile) {
      this.updateDialogPosition();
    }
  }

  /**
   * Extract program from event/slot
   */
  extractProgram(): void {
    if (this.slotKind === SlotKind.program) {
      const programs = this.store.selectSnapshot(
        ProgramsState.allNotEmptyProgramsForDropdown,
      );
      this.program = programs.find(v => v.id === this.slotProgram) as SlotProgram;
    }
  }

  /**
   * Extract playlist from event/slot
   */
  extractPlaylist(): void {
    if (this.slotKind === SlotKind.playlist) {
      const playlists = this.store.selectSnapshot(
        SchedulerCoreState.allPlaylistsForDropdown,
      );
      this.playlist = playlists.find(v => v.id === this.slotPlaylist) as SlotPlaylist;
    }
  }

  /**
   * Extract play type icon from event/slot
   */
  extractPlayTypeIcon(): void {
    if (this.slotKind === SlotKind.program) {
      this.playTypeIcon = 'fa-stopwatch';
    } else if (this.slotKind === SlotKind.playlist) {
      this.playTypeIcon = 'fa-outdent';
    }
  }

  /**
   * Extract play type text from event/slot
   */
  extractPlayTypeText(): void {
    if (this.slotKind === SlotKind.program && this.slotPlayType === 'sequential') {
      this.playTypeText = marker(
        'SCHEDULE.PLANNING.SLOT_POPOVER_PLAY_TYPE_TEXT_PROGRAM_ORDER',
      );
    } else if (this.slotKind === SlotKind.program && this.slotPlayType === 'random') {
      this.playTypeText = marker(
        'SCHEDULE.PLANNING.SLOT_POPOVER_PLAY_TYPE_TEXT_PROGRAM_RANDOM',
      );
    } else if (
      this.slotKind === SlotKind.playlist &&
      this.slotPlayType === 'sequential'
    ) {
      this.playTypeText = marker(
        'SCHEDULE.PLANNING.SLOT_POPOVER_PLAY_TYPE_TEXT_PLAYLIST_ORDER',
      );
    } else if (this.slotKind === SlotKind.playlist && this.slotPlayType === 'random') {
      this.playTypeText = marker(
        'SCHEDULE.PLANNING.SLOT_POPOVER_PLAY_TYPE_TEXT_PLAYLIST_RANDOM',
      );
    }
  }

  /*
   * Format slot repeat text
   */
  extractRepeatText(): void {
    if (!this.slotRepeat) {
      return;
    }
    if (this.slotRepeat.repeatInterval === 1) {
      this.repeatText = marker('SCHEDULE.PLANNING.SLOT_POPOVER_REPEAT_EVERY_DAY');
    } else if (this.slotRepeat.repeatInterval === 7) {
      this.repeatText = marker('SCHEDULE.PLANNING.SLOT_POPOVER_REPEAT_EVERY_WEEK');
    } else if (this.slotRepeat.repeatInterval === 14) {
      this.repeatText = marker('SCHEDULE.PLANNING.SLOT_POPOVER_REPEAT_EVERY_2_WEEKS');
    }
    this.repeatWeekday = this.translate
      .instant('SCHEDULE.PLANNING.EDIT_MODAL_LABEL_ENDS_ON')
      .toLowerCase();
    // Cast to string in case of only 1 day
    const days = `${this.slotRepeat.repeatDays}`.split(',').filter(d => !!d);
    days.forEach((d, i) => {
      this.repeatWeekday += moment().day(d).format('dddd');
      if (i + 2 < days.length) {
        this.repeatWeekday += ', ';
      } else if (i + 2 === days.length) {
        this.repeatWeekday += ` ${this.translate.instant(
          'MEDIA.FILTERS.NUMERIC_INPUT.AND',
        )} `;
      }
    });
  }

  /**
   * Format an event/slot date range
   */
  formatDateRange(): void {
    this.formattedDateRange = this.event.formatRange({
      weekday: 'short',
      day: 'numeric',
      month: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      omitZeroMinute: true,
    });
  }

  /**
   * Update dialog's position depending on event/slot's one
   *
   * Dialog must be centered vertically in window and be stuck to the event/slot
   * Inspired from Google Agenda
   */
  updateDialogPosition(): void {
    // Variables are using pixel unit
    const windowHeight = window.innerHeight;
    const {
      width: dialogOuterWidth,
      height: dialogOuterHeight,
      margin: dialogMargin,
    } = this.dialogDimensions;
    const topMaxPosition = 10;
    const slotRect = this.clickInfo.el.getBoundingClientRect();
    const clickLocation = {
      top: this.clickInfo.jsEvent.clientY,
      left: this.clickInfo.jsEvent.clientX,
    };

    // Center-left in window initially
    let x = clickLocation.left - 85 - dialogOuterWidth + dialogMargin / 2;
    let y = windowHeight / 2 - dialogOuterHeight / 2;

    // Move to right if overflow on left
    if (x < 0) {
      x = clickLocation.left + slotRect.width - dialogMargin / 2;
    }

    // Dialog's top position cannot be below slot's top position (align dialog and slot on top)
    if (y + dialogMargin > clickLocation.top) {
      y = clickLocation.top - dialogMargin;
    }

    // Dialog's bottom position cannot be above slot's top position (stick dialog to slot on corners)
    if (y + dialogOuterHeight - dialogMargin / 2 < clickLocation.top) {
      y = clickLocation.top - dialogOuterHeight + dialogMargin / 2;
    }

    // Prevent overflow on top
    if (y < topMaxPosition) {
      y = topMaxPosition;
    }

    this.dialogRef.updatePosition({
      left: `${x}px`,
      top: `${y}px`,
    });
  }

  /**
   * "Delete" button clicked
   */
  deletionClicked(event: EventApi): void {
    this.slotDeletion.emit(event);
  }

  /**
   * "Edit" button clicked
   */
  editionClicked(event: EventApi): void {
    this.slotEdition.emit(event);
  }

  /**
   * Close self (closing dialog)
   */
  closeSelf(): void {
    this.dialogRef.close();
  }
}
